/**
 * Product Selector Module
 * Handles design group and product selection functionality with accessibility support
 */
const selectors = {
  container: '[data-section-type="product"]',
  designOptions: '.design-selector__option',
  productOptions: '.product-selector__option',
  productGroups: '.product-selector__group',
  productDetails: '.product-details',
  gallery: '.product-gallery'
};

const classes = {
  selected: 'is-selected',
  hidden: 'hidden',
  loading: 'is-loading'
};

class ProductSelector {
  constructor(container) {
    this.container = container;
    this.currentProductHandle = null;
    this.currentDesignGroup = null;
    
    // Get initial states
    const selectedDesign = this.container.querySelector(`${selectors.designOptions}.is-selected`);
    const selectedProduct = this.container.querySelector(`${selectors.productOptions}.is-selected`);
    
    if (selectedDesign) {
      this.currentDesignGroup = selectedDesign.dataset.designGroup;
    }
    if (selectedProduct) {
      this.currentProductHandle = selectedProduct.dataset.productHandle;
    }
    
    // Remove loading state on initialization
    this.container.classList.remove('is-loading');
    
    this.initEventListeners();
  }

  initEventListeners() {
    // Design group selection
    const designButtons = this.container.querySelectorAll(selectors.designOptions);
    designButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleDesignSelection(e));
    });

    // Product selection
    const productButtons = this.container.querySelectorAll(selectors.productOptions);
    productButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleProductSelection(e));
    });
  }

  async handleDesignSelection(event) {
    const selectedButton = event.currentTarget;
    const designGroup = selectedButton.dataset.designGroup;
    
    if (designGroup !== this.currentDesignGroup) {
      this.setLoadingState(true);
      
      try {
        // Update design button states
        const designButtons = this.container.querySelectorAll(selectors.designOptions);
        designButtons.forEach(button => {
          const isSelected = button === selectedButton;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-checked', isSelected);
          button.setAttribute('tabindex', isSelected ? '0' : '-1');
        });

        // Update the design group label
        const designLabel = this.container.querySelector('.design-selector__label-value');
        if (designLabel) {
          const shortName = selectedButton.dataset.designShortName;
          designLabel.textContent = shortName || designGroup;
        }

        // Update product groups visibility
        const productGroups = this.container.querySelectorAll(selectors.productGroups);
        productGroups.forEach(group => {
          const groupDesignGroup = group.dataset.designGroup;
          const isVisible = groupDesignGroup === designGroup;
          
          group.style.display = isVisible ? '' : 'none';
          group.setAttribute('aria-hidden', !isVisible);
          
          if (isVisible) {
            const firstProduct = group.querySelector(selectors.productOptions);
            if (firstProduct) {
              this.handleProductSelection({ currentTarget: firstProduct });
            }
          }
        });

        this.currentDesignGroup = designGroup;
        
      } catch (error) {
        console.error('Error updating design display:', error);
      } finally {
        this.setLoadingState(false);
      }
    }
  }

  async handleProductSelection(event) {
    const selectedButton = event.currentTarget;
    const productHandle = selectedButton.dataset.productHandle;
    
    if (productHandle !== this.currentProductHandle) {
      this.setLoadingState(true);
      
      try {
        // Update button states
        const productButtons = selectedButton.closest(selectors.productGroups)
          .querySelectorAll(selectors.productOptions);
        
        productButtons.forEach(button => {
          const isSelected = button === selectedButton;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-checked', isSelected);
          button.setAttribute('tabindex', isSelected ? '0' : '-1');
        });

        // Update the product quantity label
        const barsQuantity = selectedButton.querySelector('.product-selector__quantity')?.textContent.split(' ')[0];
        const quantityLabel = this.container.querySelector('.product-selector__label-value');
        if (quantityLabel && barsQuantity) {
          quantityLabel.textContent = barsQuantity;
        }

        // Update gallery
        await this.updateProductDisplay(productHandle);
        
        // Store current product handle
        this.currentProductHandle = productHandle;
        
        // Update checkout button visibility
        const checkoutButtons = document.querySelectorAll('[data-checkout-button]');
        checkoutButtons.forEach(button => {
          const isVisible = button.dataset.productHandle === productHandle;
          button.style.display = isVisible ? '' : 'none';
        });
      } catch (error) {
        console.error('Error updating product display:', error);
      } finally {
        this.setLoadingState(false);
      }
    }
  }

  setLoadingState(isLoading) {
    this.container.classList.toggle('is-loading', isLoading);
  }

  async updateProductDisplay(productHandle) {
    try {
      // Get the gallery container
      const galleryContainer = this.container.querySelector('.product-block-list__item--gallery');
      if (!galleryContainer) {
        console.error('Gallery container not found');
        return;
      }
      
      // Fetch product data
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Failed to fetch product data');
      
      const productData = await response.json();
      
      // Get gallery elements
      const carousel = galleryContainer.querySelector('.product-gallery__carousel');
      const thumbnailList = galleryContainer.querySelector('.product-gallery__thumbnail-list');
      
      if (carousel && thumbnailList && productData.media) {
        // Update carousel
        carousel.setAttribute('data-media-count', productData.media.length);
        carousel.setAttribute('data-initial-media-id', productData.media[0].id);
        
        // Update carousel items - only first one visible initially
        carousel.innerHTML = productData.media.map((media, index) => `
          <div class="product-gallery__carousel-item ${index === 0 ? 'is-selected' : ''}" 
               tabindex="-1" 
               data-media-id="${media.id}" 
               data-media-type="image"
               style="${index === 0 ? '' : 'display: none;'}">
            <div class="product-gallery__size-limiter" style="max-width: ${media.width}px">
              <div class="aspect-ratio" style="padding-bottom: 100.0%">
                <img src="${media.src}"
                     alt="${media.alt || ''}"
                     class="product-gallery__image"
                     loading="lazy"
                     width="${media.width}"
                     height="${media.height}"
                     data-zoom="${media.src}&width=1800"
                     data-zoom-width="${media.width}">
              </div>
            </div>
          </div>
        `).join('');
        
        // Update thumbnails
        thumbnailList.innerHTML = productData.media.map((media, index) => `
          <a href="${media.src}" 
             class="product-gallery__thumbnail ${index === 0 ? 'is-nav-selected' : ''}" 
             data-media-id="${media.id}">
            <img src="${media.src}&width=130"
                 alt="${media.alt || ''}"
                 loading="lazy"
                 width="130">
          </a>
        `).join('');
        
        // Initialize thumbnail click handlers
        this.initThumbnailHandlers(galleryContainer);
      }
      
    } catch (error) {
      console.error('Error in updateProductDisplay:', error);
      throw error;
    }
  }
  
  initThumbnailHandlers(galleryContainer) {
    const thumbnails = galleryContainer.querySelectorAll('.product-gallery__thumbnail');
    const carouselItems = galleryContainer.querySelectorAll('.product-gallery__carousel-item');
    
    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Update selected thumbnail state
        thumbnails.forEach(t => t.classList.remove('is-nav-selected'));
        thumbnail.classList.add('is-nav-selected');
        
        // Update carousel item visibility
        const mediaId = thumbnail.dataset.mediaId;
        carouselItems.forEach(item => {
          const isSelected = item.dataset.mediaId === mediaId;
          item.classList.toggle('is-selected', isSelected);
          item.style.display = isSelected ? '' : 'none';
        });
      });
    });
  }
}

function addToCartAndCheckout(event, variantId, sellingPlanId) {
  // Prevent the default link behavior
  event.preventDefault();

  // Step 1: Clear the cart
  fetch('/cart/clear.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    }
  })
  .then(() => {
    // Step 2: Add the product with the selling plan
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          selling_plan: sellingPlanId
        }]
      })
    });
  })
  .then(response => response.json())
  .then(data => {
    // Step 3: Redirect to checkout
    window.location.href = '/checkout';
  })
  .catch(error => {
    console.error('Error processing cart:', error);
  });
}

// Initialize the module
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll(selectors.container);
  containers.forEach(container => {
    new ProductSelector(container);
  });
}); 
